/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import Lottie from 'react-lottie';

import { Conditional, Space, Text, Progress } from '@cashfree-intl/coherent';

import * as animationData from 'animation/loader.json';
import CustomIcon from 'components/CustomIcons';
import { Image } from 'components/Image';
import { TIME_FORMATS } from 'utils/constants';

import { formatDateToDateTimeFormat, processTimelineResponse } from '../utils';
import { SETTLEMENT_TIMELINE } from '../constants';

const getPercent = ({ isDone, progressing, lastStep }) => {
  if (isDone && !lastStep) {
    return 100;
  }
  if (isDone) {
    return 75;
  }
  if (progressing) {
    return 0;
  }
  return 0;
};

const StepProgress = ({ merchantLogo, settlementData }) => {
  const getTimelineIcon = (label, { isDone, disabled }) => {
    switch (label) {
      case SETTLEMENT_TIMELINE.INITIATED_BY_BANK.label:
        return (
          <CustomIcon
            componentName="BANK_LOGO" // eslint-disable-next-line no-nested-ternary, prettier/prettier
            fill={isDone ? '#FFFFFF' : disabled ? '#6B6C7B' : ''}
          />
        );
      case SETTLEMENT_TIMELINE.INITIATED_BY_CASHFREE.label:
        return <CustomIcon componentName="CASHFREE_LOGO" />;
      default:
        return merchantLogo ? (
          <Image
            className="merchant-logo"
            src={merchantLogo}
            alt="Merchant Logo"
          />
        ) : null;
    }
  };

  const defaultOptions = {
    loop: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
      className: '',
    },
  };

  const loadingAnimation = (
    <div className="progress-animation">
      <Lottie options={defaultOptions} height={80} width={80} />
    </div>
  );

  if (!settlementData) {
    return null;
  }

  const orderedSettlementData = processTimelineResponse([settlementData], {
    hideRetrials: true,
    reverse: true,
  });
  const { timeLine } = orderedSettlementData;

  return (
    <>
      <Space>
        {timeLine.map((timelineItem, index) => {
          const isDone = timelineItem.status === 'SUCCESS';
          const progressing =
            !isDone &&
            (index === 0 || timeLine[index - 1].status === 'SUCCESS');
          const disabled = !isDone && !progressing;
          const percent = getPercent({
            progressing,
            isDone,
            firstStep: index === 0,
            lastStep: index === timeLine.length - 2,
          });

          return (
            <div className="step-progress" key={timelineItem.label}>
              <Space alignItems="center">
                <div
                  className={`step-progress-icon mr-left-2 mr-right-2 mr-top-5 ${
                    disabled ? 'disabled' : ''
                  } ${isDone ? 'done' : ''}`}
                >
                  <Conditional if={progressing}>{loadingAnimation}</Conditional>
                  {getTimelineIcon(timelineItem.label, { isDone, disabled })}
                </div>
                <Conditional if={index !== timeLine.length - 1}>
                  <Progress
                    className={`cf-progress-bar step-progress-bar m-0 ${
                      percent <= 0 ? 'empty-progress-bar' : ''
                    } ${percent === 100 ? 'full-progress-bar' : ''}`}
                    percent={percent}
                    size="xs"
                  />
                </Conditional>
              </Space>
              <div className="mr-top-5">
                <Space gap={1}>
                  <Conditional if={!progressing && !disabled}>
                    <CustomIcon
                      componentName="GREEN_TICK_SMALL"
                      className="mr-top-2"
                      dimension="11"
                    />
                  </Conditional>
                  <div>
                    <Text variant="b12">{timelineItem.label}</Text>
                    <Text
                      color="bodyLight"
                      className="tiny-text report-type-description pr-1"
                    >
                      {timelineItem.time
                        ? // eslint-disable-next-line prettier/prettier
                          formatDateToDateTimeFormat(
                            timelineItem.time,
                            TIME_FORMATS.DATE_MONTH_YEAR_WITH_TIME,
                          )
                        : ''}
                    </Text>
                  </div>
                </Space>
              </div>
            </div>
          );
        })}
      </Space>
      <Text
        variant="b12"
        color="bodyLight"
        className="report-type-description font-normal mr-top-8 mr-btm-16"
      >
        {orderedSettlementData.remarks}
      </Text>
    </>
  );
};

StepProgress.propTypes = {
  merchantLogo: PropTypes.string.isRequired,
  settlementData: PropTypes.object.isRequired,
};

export default StepProgress;
